import React from 'react';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

/**
 * (C) Copyright IBM Corp. 2021.
 *
 * Licensed under the MIT License (the "License"); you may not use this file except in compliance with
 * the License. You may obtain a copy of the License at
 *
 * https://opensource.org/licenses/MIT
 *
 * Unless required by applicable law or agreed to in writing, software distributed under the License is distributed on
 * an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied. See the License for the
 * specific language governing permissions and limitations under the License.
 *
 */
var DEFAULT_BASE_URL = 'https://web-chat.global.assistant.watson.appdomain.cloud';
// When withWebChat is first used, loadWebChatScript promise will be set to loadWebChatScript(baseUrl).
// This way we can have multiple withWebChat instances listen to this same promise.
var loadWebChatScriptPromise;
/**
 * Injects a method to create an instance of web chat into the props for the component.
 *
 * withWebChat takes a component and a config argument. The config argument contains two parameters.
 * First, a "debug" option that takes a boolean. When turned on, this will add console.logs outlying the status of the
 * web chat during each step of build and tear down. The second parameter is baseUrl. This is used to identify where
 * the web chat should load from. config.baseUrl is used for internal debugging and development purposes, so you
 * shouldn't have to touch that normally.
 */
function withWebChat(passedConfig) {
    if (passedConfig === void 0) { passedConfig = {}; }
    return function withWebChatWithConfig(WrappedComponent) {
        var displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
        function WithWebChat(props) {
            var config = {
                baseUrl: passedConfig.baseUrl || DEFAULT_BASE_URL,
                debug: passedConfig.debug || false,
            };
            // We track the passed web chat config and the promise that the web chat script has been loaded in state.
            var _a = React.useState(), webChatConfig = _a[0], setWebChatConfig = _a[1];
            var webChatLoadedPromise = React.useRef(new Deferred());
            /**
             * This function is passed as a prop. We are not able to immediately work to create an instance of web chat until
             * the external script loads so we await createWebChatInstanceReady being resolved. We also need to be able to keep
             * track of if the component is still mounted in all our promises, so we use createWebChatInstanceInternal that is
             * able to keep track of mounted state.
             */
            function createWebChatInstance(passedWebChatConfig) {
                return __awaiter(this, void 0, void 0, function () {
                    var createWebChatInstanceInternal;
                    return __generator(this, function (_a) {
                        switch (_a.label) {
                            case 0:
                                setWebChatConfig(passedWebChatConfig);
                                return [4 /*yield*/, webChatLoadedPromise.current.promise];
                            case 1:
                                createWebChatInstanceInternal = _a.sent();
                                return [2 /*return*/, createWebChatInstanceInternal(passedWebChatConfig)];
                        }
                    });
                });
            }
            // This effect only runs when createWebChatInstance is called. It contains a lot of promises while loading up web chat.
            // Each of these promises checks if isMounted is still set to true before continuing. When the component unmounts,
            // isMounted is set to false. This gives us safety from quick mounting and unmounting that can create problems
            // if we don't stop the promises from continuing to run.
            React.useEffect(function () {
                var isMounted = true;
                var instance;
                // If config.debug is true, this method will console.log out the passed messages, prefixed by an identifier to
                // separate from non web chat console.log traffic.
                function logger() {
                    var args = [];
                    for (var _i = 0; _i < arguments.length; _i++) {
                        args[_i] = arguments[_i];
                    }
                    if (config.debug) {
                        // eslint-disable-next-line no-console
                        console.log("[Watson Assistant withWebChat".concat(webChatConfig && webChatConfig.namespace ? ": Namespace \"".concat(webChatConfig.namespace, "\"") : '', "]"), args);
                    }
                }
                // This function manages all the steps to load a web chat and pass the instance back in a promise. It manages making sure
                // the component is still mounted as it runs through async steps and any clean up of the instance on failures.
                // eslint-disable-next-line consistent-return
                function createWebChatInstanceTemplate(passedWebChatConfig) {
                    return __awaiter(this, void 0, void 0, function () {
                        var onLoadReference, error_1;
                        return __generator(this, function (_a) {
                            switch (_a.label) {
                                case 0:
                                    if (!isMounted) return [3 /*break*/, 7];
                                    logger('creating web chat instance');
                                    onLoadReference = passedWebChatConfig.onLoad || function noop() { };
                                    _a.label = 1;
                                case 1:
                                    _a.trys.push([1, 5, , 6]);
                                    if (instance) {
                                        // eslint-disable-next-line no-console
                                        console.warn('[Watson Assistant withWebChat] createWebChatInstance has already been called... destroying previous instance.');
                                        instance.destroy();
                                    }
                                    if (!window.loadWatsonAssistantChat) return [3 /*break*/, 3];
                                    logger('web chat instance being created');
                                    return [4 /*yield*/, window.loadWatsonAssistantChat(passedWebChatConfig)];
                                case 2:
                                    instance = _a.sent();
                                    return [3 /*break*/, 4];
                                case 3: throw new Error('window.loadWatsonAssistantChat is undefined');
                                case 4:
                                    if (isMounted) {
                                        logger('web chat instance created and returned to component');
                                        onLoadReference(instance);
                                    }
                                    else if (instance) {
                                        logger('web chat instance created but component unmounted...destroying instance and aborting.');
                                        instance.destroy();
                                    }
                                    return [2 /*return*/, instance];
                                case 5:
                                    error_1 = _a.sent();
                                    if (instance) {
                                        logger('web chat failed to create instance...destroying instance and aborting.');
                                        instance.destroy();
                                    }
                                    else {
                                        logger('web chat failed to create instance...aborting.');
                                    }
                                    // Throw the error back upstream to be handled inside the component.
                                    throw new Error(error_1);
                                case 6: return [3 /*break*/, 8];
                                case 7:
                                    logger('web chat instance ready to be created, but component unmounted...aborting.');
                                    return [2 /*return*/, instance];
                                case 8: return [2 /*return*/];
                            }
                        });
                    });
                }
                if (webChatConfig) {
                    logger('createWebChatInstance called');
                    // If the script tag for web chat has not been injected on the page, do so now.
                    if (!loadWebChatScriptPromise) {
                        logger('appending web chat scripts to body');
                        loadWebChatScriptPromise = loadWebChatScript(webChatConfig, config.baseUrl);
                    }
                    loadWebChatScriptPromise
                        .then(function () {
                        logger('web chat script loaded');
                        if (isMounted) {
                            logger('web chat script loaded and component is still mounted. Setting createWebChatInstance.');
                            webChatLoadedPromise.current.resolve(createWebChatInstanceTemplate);
                        }
                        else {
                            logger('web chat script loaded and component is no longer mounted...aborting.');
                        }
                    })
                        .catch(function (error) {
                        logger('web chat script failed', error);
                        if (isMounted) {
                            logger('web chat script failed to load. createWebChatInstance will reject.');
                            webChatLoadedPromise.current.reject("[Watson Assistant withWebChat".concat(webChatConfig && webChatConfig.namespace ? ": Namespace \"".concat(webChatConfig.namespace, "\"") : '', "] web chat failed to load."));
                        }
                        else {
                            logger('web chat script failed to load and component is no longer mounted...aborting.');
                        }
                    });
                }
                return function () {
                    // By setting isMounted to false, we prevent post async code from running when the component is no longer mounted.
                    isMounted = false;
                    if (webChatConfig) {
                        if (instance) {
                            logger('component has unmounted...destroying web chat instance and aborting');
                            instance.destroy();
                        }
                        else {
                            logger('component has unmounted before web chat instance was created...aborting');
                        }
                    }
                };
            }, [webChatConfig, webChatLoadedPromise]);
            var _b = props, forwardedRef = _b.forwardedRef, restPropsTemp = __rest(_b, ["forwardedRef"]);
            var rest = restPropsTemp;
            return React.createElement(WrappedComponent, __assign({}, rest, { ref: forwardedRef, createWebChatInstance: createWebChatInstance }));
        }
        var WithForwardedRef = React.forwardRef(function (props, ref) { return (React.createElement(WithWebChat, __assign({}, props, { forwardedRef: ref }))); });
        WithForwardedRef.displayName = displayName;
        return WithForwardedRef;
    };
}
/**
 * A class to mirror the old jQuery deferred to allow exposing of resolve, reject to external scripts to call.
 *
 * @see https://github.com/domenic/promises-unwrapping/blob/master/docs/states-and-fates.md
 */
var Deferred = /** @class */ (function () {
    function Deferred() {
        var _this = this;
        this.state = 'pending';
        this.fate = 'unresolved';
        this.promise = new Promise(function (resolve, reject) {
            _this._resolve = resolve;
            _this._reject = reject;
        });
        this.promise.then(function () {
            _this.state = 'fulfilled';
        }, function () {
            _this.state = 'rejected';
        });
    }
    Deferred.prototype.resolve = function (value) {
        this.fate = 'resolved';
        this._resolve(value);
    };
    Deferred.prototype.reject = function (reason) {
        this.fate = 'resolved';
        this._reject(reason);
    };
    Deferred.prototype.isResolved = function () {
        return this.fate === 'resolved';
    };
    Deferred.prototype.isPending = function () {
        return this.state === 'pending';
    };
    Deferred.prototype.isFulfilled = function () {
        return this.state === 'fulfilled';
    };
    Deferred.prototype.isRejected = function () {
        return this.state === 'rejected';
    };
    return Deferred;
}());
// Inject WatsonAssistantChatEntry.js on the page and return a promise that resolves successfully onload.
function loadWebChatScript(webChatConfig, baseUrl) {
    var src = "".concat(baseUrl.replace(/\/$/, ''), "/versions/").concat(webChatConfig.clientVersion || 'latest', "/WatsonAssistantChatEntry.js");
    return new Promise(function (resolve, reject) {
        var scriptElement = document.createElement('script');
        scriptElement.setAttribute('id', 'with-web-chat');
        scriptElement.setAttribute('async', 'true');
        scriptElement.onload = function () { return resolve(); };
        scriptElement.onerror = function () { return reject(); };
        scriptElement.src = src;
        document.head.appendChild(scriptElement);
    });
}

export { withWebChat };
